import { ListHeader, ListToolbar } from "@seagames/ui";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { QuotaEditor } from "../../components/quotas/quota-editor";
import { QuotaOverview } from "../../components/quotas/quota-overview";
import { QuotaRulesPanel } from "../../components/quotas/quota-rules-panel";
import { requireAdminRole } from "../../lib/auth/guards";
import { gamesOptionsOptions, useDefaultGamesId } from "../../lib/competition/games";
import { mainFunctionOptionsOptions } from "../../lib/functions/queries";
import { quotaUsageOptions } from "../../lib/quotas/queries";
import { responsibleOrganizationOptionsOptions } from "../../lib/responsible-organizations/queries";

/** URL = the page's state. Lenient: a hand-edited value falls back to "not set". */
const searchSchema = z.object({
	games_id: z.string().uuid().optional().catch(undefined),
	responsible_organization_id: z.string().uuid().optional().catch(undefined),
});

/**
 * Game Settings › Quotas (M4): how many cards each organisation may register
 * per category in one games, with optional women / men bounds, and the rules
 * checked against the live counts. Over quota is shown on Records, never
 * blocked. ADMIN reads; SUPER_ADMIN edits (the API enforces both).
 *
 * Games (default: the main games) and organisation live in the URL and apply
 * on click. No organisation = the overview of every organisation; one =
 * its editable table. Rules are per games, below either.
 */
export const Route = createFileRoute("/_app/settings/quotas")({
	beforeLoad: ({ context }) => requireAdminRole(context.session),
	validateSearch: searchSchema,
	component: QuotasPage,
});

function QuotasPage() {
	const { t } = useTranslation("quotas");
	const search = Route.useSearch();
	const navigate = Route.useNavigate();
	const { session } = Route.useRouteContext();
	const canWrite = session.active_role === "SUPER_ADMIN";

	const games = useQuery(gamesOptionsOptions());
	const gamesId = useDefaultGamesId(search.games_id);
	const orgs = useQuery(responsibleOrganizationOptionsOptions());
	const categories = useQuery(mainFunctionOptionsOptions());
	const orgId = search.responsible_organization_id;
	const org = orgs.data?.find((o) => o.responsible_organization_id === orgId);
	const usage = useQuery({ ...quotaUsageOptions({ games_id: gamesId ?? "", responsible_organization_id: orgId }), enabled: gamesId !== undefined });
	// keepPreviousData would show the overview's numbers under the editor for a moment — only trust a matching answer.
	const usageData = usage.data && usage.data.games_id === gamesId && (usage.data.responsible_organization_id ?? undefined) === orgId ? usage.data : undefined;

	const patch = (p: Partial<z.infer<typeof searchSchema>>) => void navigate({ search: (s) => ({ ...s, ...p }), replace: false });

	return (
		<div className="flex min-w-0 flex-col gap-[1.125rem]">
			<ListHeader title={t("page.title")} description={t("page.intro")} />

			<ListToolbar
				chips={[
					...((games.data?.length ?? 0) > 1
						? [
								{
									kind: "options" as const,
									key: "games",
									label: t("filters.games"),
									options: (games.data ?? []).map((g) => ({ value: g.games_id, label: g.name })),
									value: gamesId ? [gamesId] : [],
									// The games is never "none": clicking the pressed chip keeps it.
									onChange: (v: string[]) => patch({ games_id: v[0] ?? gamesId }),
								},
							]
						: []),
					{
						kind: "menu",
						key: "organisation",
						label: t("filters.organisation"),
						options: (orgs.data ?? []).map((o) => ({ value: o.responsible_organization_id, label: o.name })),
						value: orgId ?? null,
						onChange: (v) => patch({ responsible_organization_id: v ?? undefined }),
						searchPlaceholder: t("filters.search"),
					},
				]}
				active={!!orgId}
				onClearAll={() => patch({ responsible_organization_id: undefined })}
			/>

			{usage.isError && !usageData ? (
				<div className="rounded-[0.625rem] border border-gms-line bg-base-100 px-[1rem] py-[0.875rem] text-[0.8125rem]">
					<p className="font-semibold text-gms-bad">{t("page.failed")}</p>
					<button type="button" className="btn btn-ghost btn-xs mt-[0.375rem]" onClick={() => void usage.refetch()}>
						{t("page.retry")}
					</button>
				</div>
			) : orgId ? (
				usageData && categories.data && orgs.data ? (
					<QuotaEditor
						key={`${gamesId}:${orgId}`}
						gamesId={gamesId!}
						organisation={{ id: orgId, name: org?.name ?? t("card.oneOrganisation"), typeId: org?.organization_type?.id ?? null }}
						usage={usageData}
						categories={categories.data}
						canWrite={canWrite}
					/>
				) : (
					<div className="grid gap-[0.5rem]">
						{[0, 1, 2, 3, 4].map((i) => (
							<div key={i} className="h-[2.5rem] animate-pulse rounded-[0.375rem] bg-gms-soft-2" />
						))}
					</div>
				)
			) : (
				<QuotaOverview usage={usageData} organisations={orgs.data ?? []} loading={!usageData || orgs.isPending} onPick={(id) => patch({ responsible_organization_id: id })} />
			)}

			{gamesId && <QuotaRulesPanel gamesId={gamesId} canWrite={canWrite} />}
		</div>
	);
}
